import { useAuth0 } from "@auth0/auth0-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useToken } from "../../../../server/auth/use-token";
import { toISO8601 } from "../../../../utils/iso8601";
import { usePendo } from "../../../analytics/hooks/use-pendo-watch";
import { useClaims } from "../../../generic/hooks/use-claims";
import { RoundaboutEntrance } from "./roundabout-entrance";
import { build } from "./roundabout-roadcrew";
import { makeUserDetails } from "./roundabout-utilities";

import type { ISO8601 } from "../../../../utils/iso8601";
import type {
  RoundaboutContextProps,
  RoundaboutCurrentRole,
  RoundaboutOnSyncIn,
  RoundaboutRoomUser,
  RoundaboutTrackPendoEvent,
} from "./roundabout-markings";
import type { FC, ReactNode } from "react";

export const RoundaboutContext = createContext<RoundaboutContextProps>({
  currentOtherUsers: [],
  userReadOnlyRole: false,
  captureMomentFormSave: () => {},
});
// This instantiates a Supabase Presence connection, and tracks how many users are
// editing a specific moment (ie the current moment).
// Only 1 WS client can be instantiated at a time, and the output is rendered in a few places,
// so this a context provider feels the most appropriate.
// This also renders the popups advising the user what to do.
export const RoundaboutContextProvider: FC<{
  children: ReactNode;
  momentId?: string;
  reloadMoment: () => void;
  // Indicates the user's current activity on the page, such as editing or viewing.
  // This allows us to distinguish reviewers (people on the review page not edit page),
  // so we can avoid showing them dialogs like "Warning: user is editing".
  fixedRole?: RoundaboutCurrentRole;
}> = ({ children, momentId, reloadMoment, fixedRole }) => {
  const pendo = usePendo();
  const { user } = useAuth0();
  const claims = useClaims();
  const getAccessToken = useToken();
  const [myRole, setMyRole] = useState<RoundaboutCurrentRole | null>(fixedRole ?? null);
  const [myMomentFormLastSaveAt, setMyMomentFormLastSaveAt] = useState<ISO8601 | null>(null);

  // Operating from the single sync event means we can create a single source of truth
  // for the current state and trigger everything else from it.
  const [roomUsers, setRoomUsers] = useState<RoundaboutRoomUser[] | null>(null);

  // USing ref as we don't need a rerender; it will only cleanup if there is a reason.
  const cleanupRef = useRef<() => void | null>(null);
  useEffect(
    () =>
      // Cleanup the WS connection when the component unmounts
      () => {
        if (cleanupRef.current) {
          cleanupRef.current();
        }
      },
    [],
  );
  const handleSyncOut = useMemo(() => {
    // oxlint-disable-next-line react/refs -- pre-existing: cleanup handle deliberately kept in a ref (see comment above) and touched from useMemo
    if (cleanupRef.current) {
      // oxlint-disable-next-line react/refs -- pre-existing: cleanup handle deliberately kept in a ref (see comment above) and touched from useMemo
      cleanupRef.current();
    }
    if (!claims || !user || !momentId) {
      // This is also operates as an escape pod in case there is a problem
      // As the ws has the ability to block editing moments we need the ability
      // to quickly disable it for our users.
      return null;
    }

    const onSyncIn: RoundaboutOnSyncIn = (state) => {
      const syncRoomUsers = Object.values(state).flatMap((value) => value);
      setRoomUsers(syncRoomUsers);
    };

    // Flags are loaded, user is loaded, time to initialise.
    const roundabout = build({
      momentId,
      user: claims,
      getAccessToken,
      onSyncIn,
    });
    // oxlint-disable-next-line react/refs -- pre-existing: cleanup handle deliberately kept in a ref (see comment above) and touched from useMemo
    cleanupRef.current = roundabout.cleanup;
    return roundabout.handleSyncOut;
  }, [claims, user, momentId, getAccessToken]);

  const indicate = useCallback(async () => {
    // The room track is the current state of the room for all users
    // When my situation changes, echo it into the room.
    if (!user || !handleSyncOut) {
      return;
    }
    // Announce our presence
    const trackEvent: Omit<RoundaboutRoomUser, "presence_ref"> = {
      ...makeUserDetails(user),
      role: myRole,
      sessionLastSeenAt: toISO8601(new Date()),
      momentFormLastSaveAt: myMomentFormLastSaveAt,
    };
    await handleSyncOut(trackEvent);
  }, [myRole, myMomentFormLastSaveAt, user, handleSyncOut]);

  useEffect(() => {
    // Implicitly tell the room of any state updates for me
    void indicate();
  }, [indicate]);

  const trackPendoEvent = useCallback<RoundaboutTrackPendoEvent>(
    (suffix: string, metadata: pendo.Metadata = {}) => {
      pendo?.track(`moment-editor-clash-${suffix}`, {
        momentId: momentId || null,
        ...metadata,
      });
    },
    [pendo, momentId],
  );

  // The context values

  const currentOtherUsers = useMemo(() => {
    // Flags for testing to hide the avatars.

    // If the user is not present, or the roomUsers are not loaded, show no avatars.
    if (!user || !roomUsers) {
      return [];
    }
    return roomUsers.filter((roomUser) => roomUser.userId !== user.sub);
  }, [roomUsers, user]);

  const captureMomentFormSave = useCallback(() => {
    try {
      setMyMomentFormLastSaveAt(toISO8601(new Date()));
    } catch (error) {
      console.error("Error capturing Communication form save", error);
    }
  }, []);

  const userReadOnlyRole = useMemo(() => myRole === "Viewing", [myRole]);
  return (
    <RoundaboutContext.Provider
      // oxlint-disable-next-line react/jsx-no-constructed-context-values -- Context value is rebuilt each render; memoising it is a separate change
      value={{
        currentOtherUsers,
        userReadOnlyRole,
        captureMomentFormSave,
      }}
    >
      {children}
      {!fixedRole && claims && roomUsers && (
        <RoundaboutEntrance
          roomUsers={roomUsers}
          mySub={claims.sub}
          myRole={myRole}
          setMyRole={setMyRole}
          reloadMoment={reloadMoment}
          indicate={indicate}
          trackPendoEvent={trackPendoEvent}
        />
      )}
    </RoundaboutContext.Provider>
  );
};

export const useRoundaboutContext = (): RoundaboutContextProps => {
  const roundaboutContext = useContext(RoundaboutContext);
  if (!roundaboutContext) {
    // Defensive, as this has breaking potential.
    console.warn("useRoundaboutContext must be used within a RoundaboutContextProvider");
    return {
      currentOtherUsers: [],
      userReadOnlyRole: false,
      captureMomentFormSave: () => {},
    };
  }
  return roundaboutContext;
};
